import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AppointmentDetail, AppointmentStatus } from '../../types/appointment';
import { fetchAppointmentsByDate, postCheckIn, postCheckOut, fetchAcommpanantesByVisitaId } from '../../services/General/citaServiceGraph';
import { fetchBuildings } from '../../services/Administracion/buildingsService';
import { formatTimeFromISO, formatDateToText, getLocalDateString } from '../../utils/fechaUtil';
import {
  getStatusIcon,
  getStatusColor,
  getStatusText,
  getTimeRemainingText,
  getTimeStatus
} from '../../utils/appointmentHelpers';
import { Building } from '../../types/building';
import { Parking } from '../../types/parking';
import { fetchParkings } from '../../services/Administracion/parkingsService';
import { Acompanante } from '../../types/acompanantes';


const AccessControlVisits: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'scheduled' | 'inProgress' | 'completed'>('scheduled');
  const [appointments, setAppointments] = useState<AppointmentDetail[]>([]);
  const [acompanantes, setAcompanantes] = useState<Acompanante[]>([]);
  const [dateFilter, setDateFilter] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [parkings, setParkings] = useState<Parking[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentDetail | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<number | null>(null);
  const [selectedParking, setSelectedParking] = useState<number | null>(null);
  const [annotations, setAnnotations] = useState('');
  const [companions, setCompanions] = useState('');
  const [actionType, setActionType] = useState<'checkin' | 'checkout' | null>(null);

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const today = getLocalDateString();

  // Función para validar si la fecha de la cita es hoy
  /* const isAppointmentToday = (appointmentDate: string): boolean => {
    if (!appointmentDate) return false;

    // Convertir fecha de la cita a formato YYYY-MM-DD (local)
    const appointmentDateObj = new Date(appointmentDate);
    const appointmentYear = appointmentDateObj.getFullYear();
    const appointmentMonth = String(appointmentDateObj.getMonth() + 1).padStart(2, '0');
    const appointmentDay = String(appointmentDateObj.getDate()).padStart(2, '0');
    const appointmentFormatted = `${appointmentYear}-${appointmentMonth}-${appointmentDay}`;

    // Comparar con la fecha actual
    return appointmentFormatted === today;
  }; */
  const isAppointmentToday = (appointmentDate: string): boolean => {
    if (!appointmentDate) return false;

    // Extraer la fecha directamente del string sin conversión de zona horaria
    const datePart = appointmentDate.split(' ')[0]; // Obtiene '2026-03-03'

    // Si viene en formato ISO, también manejarlo
    if (datePart.includes('T')) {
      const isoDatePart = appointmentDate.split('T')[0];
      return isoDatePart === today;
    }

    return datePart === today;
  };

  // Determinar si se puede hacer check-in
  const canCheckIn = (appointment: AppointmentDetail): boolean => {
    if (!isAppointmentToday(appointment.start_date)) return false;
    return appointment.current_status === AppointmentStatus.UPCOMING ||
      appointment.current_status === AppointmentStatus.SCHEDULED ||
      appointment.current_status === AppointmentStatus.LATE;
  };

  // Determinar si se puede hacer check-out
  const canCheckOut = (appointment: AppointmentDetail): boolean => {
    if (!isAppointmentToday(appointment.start_date)) return false;
    return appointment.current_status === AppointmentStatus.IN_PROGRESS;
  };

  // Función para mostrar tooltip con motivo de deshabilitación
  const getDisabledReason = (appointment: AppointmentDetail): string => {
    if (!isAppointmentToday(appointment.start_date)) {
      return 'Solo se pueden registrar ingreso/salida para citas del día de hoy';
    }

    if (canCheckIn(appointment)) return 'Disponible para ingreso';
    if (canCheckOut(appointment)) return 'Disponible para salida';
    if (appointment.current_status === AppointmentStatus.COMPLETED) return 'Visita ya finalizada';
    if (appointment.current_status === AppointmentStatus.MISSED) return 'Visita no asistida';

    return 'No disponible';
  };

  // Cargar datos iniciales
  useEffect(() => {
    setDateFilter(today);

    const loadInitialData = async () => {
      setLoading(true);
      try {
        // Cargar edificios
        const buildingsData = await fetchBuildings();
        setBuildings(buildingsData);

        // Cargar parqueos
        const parkingsData = await fetchParkings();
        setParkings(parkingsData);

        // Cargar citas para la fecha de hoy
        await loadAppointments(today);

      } catch (error: any) {
        console.log('Error cargando datos iniciales:', error.message);
        setError('Error al cargar los datos iniciales');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Refresh automático cada 5 minutos
  useEffect(() => {
    const refreshData = () => {
      if (!loading && dateFilter) {
        loadAppointments(dateFilter);
      }
    };

    refreshIntervalRef.current = setInterval(refreshData, 5 * 60 * 1000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [dateFilter, loading]);

  // Cargar citas
  const loadAppointments = async (searchDate: string) => {
    if (!searchDate) return;

    setLoading(true);
    setError(null);
    try {
      const data = await fetchAppointmentsByDate(searchDate);
      setAppointments(data);
    } catch (error: any) {
      console.log('Error cargando citas:', error.message);
      setError('Error al cargar las citas. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambio de fecha
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setDateFilter(newDate);
    if (newDate) {
      loadAppointments(newDate);
    }
  };

  // Filtrar citas por estado para las pestañas
  const scheduledAppointments = appointments.filter(a =>
    a.current_status === AppointmentStatus.UPCOMING ||
    a.current_status === AppointmentStatus.SCHEDULED ||
    a.current_status === AppointmentStatus.LATE
  );

  const inProgressAppointments = appointments.filter(a =>
    a.current_status === AppointmentStatus.IN_PROGRESS
  );

  const completedAppointments = appointments.filter(a =>
    a.current_status === AppointmentStatus.COMPLETED ||
    a.current_status === AppointmentStatus.MISSED
  );

  // Estadísticas para mostrar en las pestañas
  const tabStats = {
    scheduled: scheduledAppointments.length,
    inProgress: inProgressAppointments.length,
    completed: completedAppointments.length,
    late: appointments.filter(a => a.current_status === AppointmentStatus.LATE).length,
    missed: appointments.filter(a => a.current_status === AppointmentStatus.MISSED).length,
    todayScheduled: scheduledAppointments.filter(a => isAppointmentToday(a.start_date)).length,
    todayInProgress: inProgressAppointments.filter(a => isAppointmentToday(a.start_date)).length,
  };

  const fetchAcompanantes = async (visitId: number) => {
    setLoading(true);
    try {
      const response = await fetchAcommpanantesByVisitaId(visitId);
      const acompanantesData = (response as any)?.data || [];
      console.log('fetchAcompanantes: Acompañantes obtenidos:', acompanantesData);
      setAcompanantes(acompanantesData);
      //return response;
    } catch (error) {
      console.error('Error fetching companions:', error);
      setError('Error al cargar los acompañantes');

    } finally {
      setLoading(false);
    }
  }

  // Abrir modal de check-in
  const handleOpenCheckInModal = async (appointment: AppointmentDetail) => {
    setSelectedAppointment(appointment);
    setActionType('checkin');

    // Valores por defecto del check-in anterior o del edificio asignado
    const defaultBuilding = appointment.checkin_building_id || appointment.appointment_building_id;
    setSelectedBuilding(defaultBuilding || null);

    if (appointment.checkin_parking_id) {
      setSelectedParking(appointment.checkin_parking_id);
    }

    if (appointment.companions) {
      setCompanions(appointment.companions);
    }

    if (appointment.checkin_notes) {
      setAnnotations(appointment.checkin_notes);
    }

    //Codigo para traer los acompañantes relacionados a la Visita
    await fetchAcompanantes(appointment.id);

  };

  // Abrir modal de check-out
  const handleOpenCheckOutModal = async (appointment: AppointmentDetail) => {
    setSelectedAppointment(appointment);
    setActionType('checkout');

    //Traer acompanantes para consulta del oficial
    await fetchAcompanantes(appointment.id);


    if (appointment.checkout_notes) {
      setAnnotations(appointment.checkout_notes);
    }
  };

  // Realizar check-in
  const handleCheckIn = async () => {
    if (!selectedAppointment || !selectedBuilding || !user) return;

    setLoading(true);
    setError(null);

    try {
      const result = await postCheckIn({
        visit_id: selectedAppointment.id,
        building_id: selectedBuilding,
        parking_id: selectedParking || undefined,
        companions: companions || "",
        annotations: annotations || "",
        user: user.User.email,
      });

      await loadAppointments(dateFilter || today);

      const buildingName = buildings.find(b => b.building_id === selectedBuilding)?.building_name || 'Edificio';
      setSuccess(`✅ Ingreso realizado para ${selectedAppointment.guest_name} - ${buildingName}`);
      resetSelection();

    } catch (error: any) {
      setError(error.message || 'Error al realizar ingreso');
    } finally {
      setLoading(false);
    }
  };

  // Realizar check-out
  const handleCheckOut = async () => {
    if (!selectedAppointment || !user) return;

    setLoading(true);
    setError(null);

    try {
      await postCheckOut({
        visit_id: selectedAppointment.id,
        annotations: annotations || "",
        user: user.User.email
      });

      await loadAppointments(dateFilter);
      setSuccess(`✅ Salida registrada para ${selectedAppointment.guest_name}`);
      resetSelection();

    } catch (error: any) {
      setError(error.message || 'Error al realizar salida');
    } finally {
      setLoading(false);
    }
  };

  // Resetear selección
  const resetSelection = () => {
    setSelectedAppointment(null);
    setSelectedBuilding(null);
    setSelectedParking(null);
    setAnnotations('');
    setCompanions('');
    setActionType(null);
    setAcompanantes([]);
  };

  // Renderizar modal unificado
  const renderModal = () => {
    if (!selectedAppointment || !actionType) return null;

    const isCheckIn = actionType === 'checkin';
    const isToday = isAppointmentToday(selectedAppointment.start_date);

    // Función para formatear la fecha de la cita
    const formatAppointmentDate = (): string => {
      try {
        if (selectedAppointment.start_date) {
          return formatDateToText(selectedAppointment.start_date);
        }
        return formatDateToText(dateFilter);
      } catch (error) {
        console.error('Error formateando fecha de cita:', error);
        return 'Fecha no disponible';
      }
    };

    return (
      // MODAL
      <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            {/* Header del modal */}
            <div className={`modal-header py-2 ${isCheckIn ? 'bg-success text-white' : 'bg-warning text-dark'}`}>
              <h6 className="modal-title mb-0">
                <i className={`fas fa-${isCheckIn ? 'sign-in-alt' : 'sign-out-alt'} me-2`}></i>
                {isCheckIn ? 'Ingreso' : 'Salida'}: {selectedAppointment.guest_name}
              </h6>
              <button type="button" className="btn-close btn-close-white" onClick={resetSelection}></button>
            </div>

            <div className="modal-body">
              {/* Información sobre la fecha de la cita */}
              <div className="alert alert-info py-2 mb-3">
                <i className="fas fa-calendar-day me-2"></i>
                <small>
                  <strong>Fecha de la cita:</strong> {formatAppointmentDate()}
                </small>
              </div>

              {/* Alerta si está atrasada */}
              {isCheckIn && selectedAppointment.current_status === 'LATE' && (
                <div className="alert alert-warning py-2 mb-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <small>
                    Esta cita tiene <strong>{selectedAppointment.minutes_since_start} minutos de retraso</strong>
                  </small>
                </div>
              )}
              {acompanantes.length > 0 && (
                <div className="mb-3">
                  <label htmlFor="" className="form-label small fw-bold">Acompañantes</label>
                  <div className="bg-light p-2 rounded" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {acompanantes.map((a, index) => (
                      <div className="d-flex justify-content-between align-items-center border-bottom py-1">
                        <span className="small">{a.nombre}</span>
                        <span className="text-muted small">{a.cedula}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isCheckIn ? (
                // Formulario de check-in
                <div className="row">
                  <div className="col-12 mb-2">
                    <label className="form-label small">Edificio Destino *</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedBuilding || ''}
                      onChange={(e) => setSelectedBuilding(e.target.value ? Number(e.target.value) : null)}
                      required
                    >
                      <option value="">Seleccionar edificio</option>
                      {buildings.map(building => (
                        <option key={building.building_id} value={building.building_id}>
                          {building.building_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12 mb-2">
                    <label className="form-label small">Parqueo Asignado</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedParking || ''}
                      onChange={(e) => setSelectedParking(e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">Sin parqueo</option>
                      {parkings.map(parking => (
                        <option key={parking.parking_id} value={parking.parking_id} title={parking.location || 'No Indica ubicación'}>
                          {parking.parking_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* <div className="col-12 mb-2">
                    <label className="form-label small">Acompañantes</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Nombres de acompañantes"
                      value={companions}
                      onChange={(e) => setCompanions(e.target.value)}
                    />
                    <small className="text-muted">Separar por comas si son varios</small>
                  </div> */}

                  <div className="col-12">
                    <label className="form-label small">Anotaciones</label>
                    <textarea
                      className="form-control form-control-sm"
                      placeholder="Observaciones del ingreso..."
                      rows={2}
                      value={annotations}
                      onChange={(e) => setAnnotations(e.target.value)}
                    />
                  </div>
                </div>
              ) : (
                // Formulario de check-out
                <div className="row">
                  <div className="col-12 mb-3">
                    <p className="small mb-2">
                      Confirmar salida de <strong>{selectedAppointment.guest_name}</strong>
                    </p>
                    {selectedAppointment.checkin_time && (
                      <p className="small text-muted mb-0">
                        <strong>Ingreso:</strong> {getTimeDisplay(selectedAppointment.checkin_time)}
                        {selectedAppointment.checked_in_by && (
                          <>
                            <br />
                            <strong>Registrado por:</strong> {selectedAppointment.checked_in_by}
                          </>
                        )}
                        <br />
                        <strong>Duración:</strong> {selectedAppointment.duration_minutes || 0} minutos
                      </p>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="form-label small">Anotaciones de la salida</label>
                    <textarea
                      className="form-control form-control-sm"
                      placeholder="Observaciones al salir..."
                      rows={2}
                      value={annotations}
                      onChange={(e) => setAnnotations(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer py-2">
              <button type="button" className="btn btn-secondary btn-sm" onClick={resetSelection}>
                Cancelar
              </button>
              <button
                type="button"
                className={`btn btn-sm ${isCheckIn ? 'btn-success' : 'btn-warning'}`}
                onClick={isCheckIn ? handleCheckIn : handleCheckOut}
                disabled={loading || (isCheckIn && !selectedBuilding)}
              >
                {loading ? 'Procesando...' : `Confirmar ${isCheckIn ? 'Ingreso' : 'Salida'}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper para mostrar hora
  const getTimeDisplay = (dateString: string | null) => {
    if (!dateString) return '-';
    return formatTimeFromISO(dateString);
  };

  // Renderizar tarjeta de cita
  const renderAppointmentCard = (appointment: AppointmentDetail) => {
    const statusColor = getStatusColor(appointment.current_status);
    const statusIcon = getStatusIcon(appointment.current_status);
    const statusText = getStatusText(appointment.current_status);
    const canDoCheckIn = canCheckIn(appointment);
    const canDoCheckOut = canCheckOut(appointment);
    const isToday = isAppointmentToday(appointment.start_date);
    const disabledReason = getDisabledReason(appointment);

    return (
      <div key={appointment.id} className={`card mb-2 border-${statusColor} ${!isToday ? 'opacity-75' : ''}`}>
        <div className="card-body p-2">
          <div className="row small align-items-center">
            {/* Icono y info principal */}
            <div className="col-1 text-center">
              <i className={`${statusIcon} text-${statusColor} fs-5`}></i>
              {!isToday && (
                <small className="d-block text-muted mt-1">
                  <i className="fas fa-calendar-day"></i>
                </small>
              )}
            </div>

            <div className="col-7">
              <div className="d-flex align-items-center">
                <strong className="me-2">{appointment.guest_name}</strong>
                {!isToday && (
                  <span className="badge bg-secondary">
                    <i className="fas fa-calendar-alt me-1"></i>
                    {new Date(appointment.start_date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                  </span>
                )}
              </div>
              <small className="text-muted">
                <span className={`badge bg-${statusColor} me-1`}>{statusText}</span>
                {appointment.current_status === AppointmentStatus.LATE && isToday && (
                  <span className="badge bg-warning text-dark ms-1">
                    {appointment.minutes_since_start} min
                  </span>
                )}
                <br />
                <strong>Cédula:</strong> {appointment.guest_cedula || 'No registrada'}
                <br />
                <strong>Hora:</strong> {getTimeDisplay(appointment.start_date)} - {getTimeDisplay(appointment.end_date)}
                <br />
                <strong>Anfitrión:</strong> {appointment.host_email}
                <br />
                <strong>Creada por:</strong> {appointment.appointment_created_by}
                {appointment.building_name && (
                  <>
                    <br />
                    <strong>Edificio:</strong> {appointment.building_name}
                  </>
                )}
                {/* Mostrar quién registró el check-in si existe */}
                {appointment.checked_in_by && (
                  <>
                    <br />
                    <small className="text-success">
                      <i className="fas fa-user-check me-1"></i>
                      Ingreso registrado por: {appointment.checked_in_by}
                    </small>
                  </>
                )}

                {appointment.appointment_host_checkout_time && (
                  <>
                    <br />
                    <small className='text-primary'>
                      <i className="fas fa-user-check me-1"></i>
                      Finalizó a las: {getTimeDisplay(appointment.appointment_host_checkout_time)}
                    </small>
                  </>
                )}

                {/* Mostrar quién registró el check-out si existe */}
                {appointment.checked_out_by && (
                  <>
                    <br />
                    <small className="text-secondary">
                      <i className="fas fa-user-times me-1"></i>
                      Salida registrada por: {appointment.checked_out_by}
                    </small>
                  </>
                )}
                {appointment.current_status === AppointmentStatus.IN_PROGRESS && appointment.checkin_time && (
                  <>
                    <br />
                    <small className="text-success">
                      <i className="fas fa-clock me-1"></i>
                      Entrada: {getTimeDisplay(appointment.checkin_time)}
                    </small>
                  </>
                )}
                {!isToday && (
                  <small className="text-danger d-block mt-1">
                    <i className="fas fa-info-circle me-1"></i>
                    Solo lectura - Cita de otro día
                  </small>
                )}
              </small>
            </div>

            {/* Botones y info adicional */}
            <div className="col-4 text-end">
              {/* Botón Ingreso */}
              {canDoCheckIn && (
                <button
                  className="btn btn-success btn-sm w-100 mb-1"
                  onClick={() => handleOpenCheckInModal(appointment)}
                  disabled={loading || !isToday}
                  title={disabledReason}
                >
                  <i className="fas fa-sign-in-alt me-1"></i>
                  Ingreso
                  {!isToday && <i className="fas fa-lock ms-1"></i>}
                </button>
              )}

              {/* Botón Check-out */}
              {canDoCheckOut && (
                <button
                  className="btn btn-warning btn-sm w-100 mb-1"
                  onClick={() => handleOpenCheckOutModal(appointment)}
                  disabled={loading || !isToday}
                  title={disabledReason}
                >
                  <i className="fas fa-sign-out-alt me-1"></i>
                  Salida
                  {!isToday && <i className="fas fa-lock ms-1"></i>}
                </button>
              )}

              {/* Información de tiempo */}
              <small className={`d-block ${appointment.current_status === AppointmentStatus.LATE ? 'text-warning' : 'text-muted'}`}>
                {getTimeStatus(appointment)}
                {!isToday && (
                  <span className="d-block text-danger">
                    <i className="fas fa-calendar-times me-1"></i>
                    No disponible
                  </span>
                )}
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Obtener texto para mostrar en las pestañas con conteo de citas de hoy
  const getTabTitle = (tab: 'scheduled' | 'inProgress' | 'completed') => {
    const baseTitles = {
      scheduled: 'Agendadas',
      inProgress: 'En Instalaciones',
      completed: 'Finalizadas'
    };

    const todayCounts = {
      scheduled: tabStats.todayScheduled,
      inProgress: tabStats.todayInProgress,
      completed: appointments.filter(a =>
        (a.current_status === 'COMPLETED' || a.current_status === 'MISSED') &&
        isAppointmentToday(a.start_date)
      ).length
    };

    const totalCounts = {
      scheduled: tabStats.scheduled,
      inProgress: tabStats.inProgress,
      completed: tabStats.completed
    };

    return (
      <>
        <i className={`fas fa-${tab === 'scheduled' ? 'calendar-alt' : tab === 'inProgress' ? 'users' : 'check-circle'} me-2`}></i>
        {baseTitles[tab]}
        <span className="badge bg-light text-dark ms-1">
          {totalCounts[tab]}
          {todayCounts[tab] > 0 && (
            <span className="text-success ms-1">({todayCounts[tab]} hoy)</span>
          )}
        </span>
      </>
    );
  };

  return (
    <div className="container-fluid p-2" style={{ maxWidth: '100%', minHeight: '100vh' }}>
      <div className="bg-primary bg-gradient text-white p-3 rounded shadow-sm mb-4">
        <div className="d-flex align-items-center justify-content-center">
          <i className="fas fa-shield-alt fa-2x me-3"></i>
          <div>
            <h2 className="mb-0">Control de Acceso de Visitantes</h2>
          </div>
        </div>
      </div>

      {/* Filtro de fecha y cabecera */}
      <div className="row mb-3">
        <div className="col-12 col-md-8 mb-2">
          <div className="d-flex align-items-center">
            <div className="me-3">
              <input
                type="date"
                className="form-control"
                value={dateFilter}
                onChange={handleDateChange}
                id="dateFilter"
              />
            </div>
            <div>
              <h6 className="mb-0">Citas para: {formatDateToText(dateFilter) || 'Selecione una fecha'}</h6>
              <small className="text-muted">
                Mostrando {appointments.length} {appointments.length === 1 ? 'cita' : 'citas'}
                {tabStats.late > 0 && `, ${tabStats.late} atrasadas`}
                {tabStats.missed > 0 && `, ${tabStats.missed} no asistidas`}
                {dateFilter !== today && (
                  <span className="text-warning d-block">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    Modo solo lectura - Las operaciones solo están disponibles para citas del día de hoy
                  </span>
                )}
              </small>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4 mb-2">
          <button
            className="btn btn-outline-info btn-sm w-100"
            onClick={() => dateFilter && loadAppointments(dateFilter)}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Cargando...
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt me-2"></i>
                Actualizar Lista
              </>
            )}
          </button>
        </div>
      </div>

      {/* Indicador de fecha actual */}
      <div className="row mb-3">
        <div className="col-12">
          <div className={`alert ${dateFilter === today ? 'alert-success' : 'alert-warning'} py-2`}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <i className={`fas fa-${dateFilter === today ? 'calendar-check' : 'calendar-alt'} me-2`}></i>
                {dateFilter === today ? (
                  <span><strong>Modo operativo:</strong> Puede realizar ingreso/salida para las citas de hoy</span>
                ) : (
                  <span><strong>Modo consulta:</strong> Solo puede visualizar citas de otros días</span>
                )}
              </div>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => {
                  setDateFilter(today);
                  loadAppointments(today);
                }}
              >
                <i className="fas fa-calendar-day me-1"></i>
                Ver citas de hoy
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs principales */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="btn-group w-100" role="group">
            <button
              type="button"
              className={`btn btn-sm ${activeTab === 'scheduled' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveTab('scheduled')}
            >
              {getTabTitle('scheduled')}
            </button>
            <button
              type="button"
              className={`btn btn-sm ${activeTab === 'inProgress' ? 'btn-warning' : 'btn-outline-warning'}`}
              onClick={() => setActiveTab('inProgress')}
            >
              {getTabTitle('inProgress')}
            </button>
            <button
              type="button"
              className={`btn btn-sm ${activeTab === 'completed' ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setActiveTab('completed')}
            >
              {getTabTitle('completed')}
            </button>
          </div>
        </div>
      </div>

      {/* Contenido de cada tab */}
      <div className="row">
        <div className="col-12">
          {/* TAB: AGENDADAS */}
          {activeTab === 'scheduled' && (
            <div>
              {scheduledAppointments.length === 0 ? (
                <div className="text-center text-muted p-4">
                  <div className="fs-1 mb-3">📅</div>
                  <p className="mb-2">No hay citas agendadas para esta fecha</p>
                  <small className="text-muted">Seleccione otra fecha o verifique las citas programadas</small>
                </div>
              ) : (
                scheduledAppointments.map(renderAppointmentCard)
              )}
            </div>
          )}

          {/* TAB: EN INSTALACIONES */}
          {activeTab === 'inProgress' && (
            <div>
              {inProgressAppointments.length === 0 ? (
                <div className="text-center text-muted p-4">
                  <div className="fs-1 mb-3">👥</div>
                  <p className="mb-2">No hay visitas en instalaciones</p>
                  <small className="text-muted">Todas las visitas han finalizado o aún no han llegado</small>
                </div>
              ) : (
                inProgressAppointments.map(renderAppointmentCard)
              )}
            </div>
          )}

          {/* TAB: FINALIZADAS */}
          {activeTab === 'completed' && (
            <div>
              {completedAppointments.length === 0 ? (
                <div className="text-center text-muted p-4">
                  <div className="fs-1 mb-3">✅</div>
                  <p className="mb-2">No hay visitas finalizadas</p>
                  <small className="text-muted">Todas las visitas están en curso o pendientes</small>
                </div>
              ) : (
                completedAppointments.map(appointment => (
                  <div key={appointment.id} className="card mb-2">
                    <div className="card-body p-2">
                      <div className="small">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong>{appointment.guest_name}</strong>
                            <span className={`badge bg-${getStatusColor(appointment.current_status)} ms-2`}>
                              {getStatusText(appointment.current_status)}
                            </span>
                          </div>
                          {!isAppointmentToday(appointment.start_date) && (
                            <span className="badge bg-secondary">
                              <i className="fas fa-calendar-alt me-1"></i>
                              {new Date(appointment.start_date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                            </span>
                          )}
                        </div>
                        <br />
                        <small className="text-muted">
                          <strong>Cédula:</strong> {appointment.guest_cedula || 'No registrada'}
                          <div className="d-flex junstify-content-between align-items-start mt-1">
                            <div className="flex-grow-1">
                              <div><strong>Ingreso:</strong> {getTimeDisplay(appointment.checkin_time || null)}</div>
                              {appointment.checked_in_by && (
                                <div className="mt-1">
                                  <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">
                                    <i className="fas fa-user-check me-1"></i>
                                    Ingreso por: {appointment.checked_in_by}
                                  </span>
                                </div>
                              )}
                            </div>
                            {appointment.appointment_host_checkout_time && (
                              <>
                                <strong>Finalizada a las:</strong>{getTimeDisplay(appointment.appointment_host_checkout_time || null)}
                                <br />
                              </>
                            )}
                          </div>
                          <strong>Salida:</strong> {getTimeDisplay(appointment.checkout_time || null)}
                          {appointment.checked_out_by && (
                            <div className="mt-1">
                              <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25">
                                <i className="fas fa-user-times me-1"></i>
                                Salida por: {appointment.checked_out_by}
                              </span>
                            </div>

                          )}
                          <br />
                          <strong>Duración:</strong> {appointment.duration_minutes || 0} minutos
                          <br />
                          <strong>Anfitrión:</strong> {appointment.host_email}
                          {appointment.checkout_notes && (
                            <>
                              <br />
                              <strong>Observaciones:</strong> {appointment.checkout_notes}
                            </>
                          )}
                          {!isAppointmentToday(appointment.start_date) && (
                            <div className="alert alert-warning mt-2 py-1">
                              <small>
                                <i className="fas fa-info-circle me-1"></i>
                                Visita registrada en otra fecha
                              </small>
                            </div>
                          )}
                        </small>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal unificado */}
      {renderModal()}

      {/* Mensajes de estado */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show py-2 mt-2" role="alert">
          <small>{error}</small>
          <button type="button" className="btn-close p-2" onClick={() => setError(null)}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show py-2 mt-2" role="alert">
          <small>{success}</small>
          <button type="button" className="btn-close p-2" onClick={() => setSuccess(null)}></button>
        </div>
      )}
    </div>
  );
};

export default AccessControlVisits;